// <OrgEmployeeCardMenu> — Org View Step 5 (T-051, spec §7).
//
// Overflow menu anchored to the MoreVertical icon on each card. Click
// outside / Esc dismisses; clicking the trigger doesn't propagate to
// the card click handler (so the card body's profile-open click
// doesn't fire alongside the menu open). HR-only rows (Quick edit,
// Deactivate) only render when the viewer is OWNER or ADMIN.

import { useEffect, useId, useRef, useState } from 'react';
import { Mail, Pencil, User as UserIcon, UserMinus } from 'lucide-react';
import type { OrgViewEmployee } from '@pa/shared';

export type OrgViewerRole = 'OWNER' | 'ADMIN' | 'MEMBER';

const HR_ROLES: ReadonlySet<OrgViewerRole> = new Set(['OWNER', 'ADMIN']);

export interface OrgEmployeeCardMenuProps {
  employee: OrgViewEmployee;
  viewerRole: OrgViewerRole;
  onOpenProfile: (id: string) => void;
  onQuickEdit: (id: string) => void;
  onSendMessage: (employee: OrgViewEmployee) => void;
  onDeactivate: (employee: OrgViewEmployee) => void;
  /** Render-prop for the trigger so the card can position it without
   *  the menu component dictating layout. The injected handlers stop
   *  click propagation per spec §7.1. */
  children: (triggerProps: {
    onClick: (e: React.MouseEvent) => void;
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
  }) => React.ReactNode;
}

export function OrgEmployeeCardMenu({
  employee,
  viewerRole,
  onOpenProfile,
  onQuickEdit,
  onSendMessage,
  onDeactivate,
  children,
}: OrgEmployeeCardMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();
  const isHr = HR_ROLES.has(viewerRole);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node | null;
      if (t && wrapperRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const triggerProps = {
    onClick: (e: React.MouseEvent) => {
      // Spec §7.1 — don't bubble to the card's onClick (which opens
      // the profile). Without this the menu would open + the profile
      // would navigate at the same time.
      e.stopPropagation();
      setOpen((v) => !v);
    },
    'aria-haspopup': 'menu' as const,
    'aria-expanded': open,
  };

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onClick={(e) => e.stopPropagation()}
    >
      {children(triggerProps)}
      {open ? (
        <div
          role="menu"
          id={menuId}
          data-testid={`org-employee-menu-${employee.id}`}
          className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-slate-100 py-2 z-50"
        >
          <MenuRow
            icon={<UserIcon size={16} aria-hidden />}
            label="View profile"
            onSelect={() => {
              setOpen(false);
              onOpenProfile(employee.id);
            }}
            testId={`org-employee-menu-view-${employee.id}`}
          />
          {isHr ? (
            <MenuRow
              icon={<Pencil size={16} aria-hidden />}
              label="Quick edit"
              onSelect={() => {
                setOpen(false);
                onQuickEdit(employee.id);
              }}
              testId={`org-employee-menu-edit-${employee.id}`}
            />
          ) : null}
          <MenuRow
            icon={<Mail size={16} aria-hidden />}
            label="Send message"
            onSelect={() => {
              setOpen(false);
              onSendMessage(employee);
            }}
            testId={`org-employee-menu-message-${employee.id}`}
          />
          {isHr && employee.status === 'active' ? (
            <MenuRow
              icon={<UserMinus size={16} aria-hidden />}
              label="Deactivate"
              onSelect={() => {
                setOpen(false);
                onDeactivate(employee);
              }}
              testId={`org-employee-menu-deactivate-${employee.id}`}
              danger
            />
          ) : null}
          {isHr && employee.status === 'inactive' ? (
            <MenuRow
              icon={<UserMinus size={16} aria-hidden />}
              label="Reactivate"
              onSelect={() => {
                setOpen(false);
                onDeactivate(employee);
              }}
              testId={`org-employee-menu-reactivate-${employee.id}`}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MenuRow({
  icon,
  label,
  onSelect,
  testId,
  danger = false,
}: {
  icon: React.ReactNode;
  label: string;
  onSelect: () => void;
  testId: string;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-left transition-colors hover:bg-slate-50 ${
        danger ? 'text-rose-700 hover:bg-rose-50' : 'text-slate-700'
      }`}
    >
      <span className="text-slate-400">{icon}</span>
      <span>{label}</span>
    </button>
  );
}
